import React from 'react';
import { Link } from 'react-router-dom';
import PageHeader from '../components/PageHeader';
import Reveal from '../components/ui/Reveal';
import SectionHeading from '../components/ui/SectionHeading';
import { categories, countIn, editorial, products } from '../data/collections';
import { useEnquiry } from '../context/EnquiryContext';
import usePageMeta from '../hooks/usePageMeta';


export default function Collections() {
  const { openEnquiry } = useEnquiry();

  usePageMeta({
    title: 'Collections | Pramukh Jewellers, Ahmedabad',
    description:
      'Browse bridal, antique jadau, temple, polki & kundan, diamond, mangalsutra, chains, rings and bangles from Pramukh Jewellers, Ahmedabad.',
    path: '/collections',
  });

  return (
    <>
      <PageHeader
        eyebrow="The Catalogue"
        title="Collections"
        lede={`${products.length} pieces across twelve houses of work — from complete bridal suites in matte 22K to slim diamond mangalsutra made for every day.`}
        image="/assets/gallery/post-13-2.jpg"
        crumbs={[{ label: 'Home', to: '/' }, { label: 'Collections' }]}
      />

      {/* Category grid */}
      <section className="bg-cream py-20 lg:py-28">
        <div className="shell">
          <div className="grid gap-x-5 gap-y-12 sm:grid-cols-2 lg:grid-cols-3">
            {categories.map((c, i) => (
              <Reveal key={c.slug} delay={(i % 3) * 90}>
                <Link to={`/collections/${c.slug}`} className="group block h-full">
                  <div className="wipe relative aspect-[4/5] overflow-hidden bg-beige/60">
                    <img
                      src={c.cover}
                      alt={c.name}
                      loading="lazy"
                      decoding="async"
                      className="h-full w-full object-cover transition-transform duration-[1500ms] ease-luxe group-hover:scale-[1.06]"
                    />
                  </div>

                  <div className="mt-4 flex items-start justify-between gap-4 border-t border-royal/10 pt-4">
                    <div>
                      <p className="text-[11.5px] tracking-wider2 text-gold-600">
                        {String(i + 1).padStart(2, '0')} — {c.native}
                      </p>
                      <h2 className="mt-2 font-display text-[1.6rem] leading-tight text-royal transition-colors duration-500 group-hover:text-gold-600">
                        {c.name}
                      </h2>
                      <p className="mt-1.5 text-[12px] uppercase tracking-wider2 text-royal/75">
                        {c.tagline}
                      </p>
                    </div>
                    <span className="mt-1 shrink-0 text-[11.5px] uppercase tracking-wider2 text-royal/70">
                      {countIn(c.slug)}
                    </span>
                  </div>
                </Link>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* Not sure where to start */}
      <section className="relative overflow-hidden bg-royal py-20 lg:py-28">
        <div className="pointer-events-none absolute inset-0 paisley-bg opacity-[0.14]" />

        <div className="shell relative grid items-center gap-12 lg:grid-cols-2">
          <div>
            <SectionHeading
              align="left"
              tone="light"
              eyebrow="Not sure where to start?"
              title={'Tell us the occasion.\nWe will shortlist.'}
              lede="Send us a photograph, a budget or just the date of the function. We will pick out what fits and keep it aside for your visit."
              className="lg:mx-0"
            />

            <Reveal delay={120} className="mt-11 flex flex-wrap items-center gap-x-10 gap-y-4">
              <button type="button" onClick={() => openEnquiry('the full catalogue', 'manual')} className="btn-line-light">
                <span className="btn-line-rule" />
                Send an enquiry
              </button>
              <Link
                to="/contact"
                className="text-[12px] font-medium uppercase tracking-luxe text-cream/70 transition-colors duration-500 hover:text-gold-200"
              >
                Visit the showroom
              </Link>
            </Reveal>
          </div>

          <Reveal delay={100} className="grid grid-cols-2 gap-4">
            {[editorial.regality, editorial.vivah].map((src, i) => (
              <div key={src} className={`wipe relative overflow-hidden ${i === 1 ? 'mt-10' : ''}`}>
                <div className="aspect-[4/5]">
                  <img src={src} alt="" loading="lazy" className="h-full w-full object-cover" />
                </div>
              </div>
            ))}
          </Reveal>
        </div>
      </section>
    </>
  );
}
